// Live Migration (per workload) — the standalone CLM opt-in list.
// Deliberately NOT part of the spot-policy dialog: a spot policy decides WHERE
// a workload's replicas land (and applying one has placement side effects);
// this list only decides HOW its pods are moved — state-preserving live
// migration instead of eviction, wherever they run. A workload on spot that is
// opted in is simply live-migrated on interruption/consolidation.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { MoveRight, Plus, Trash2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const KINDS = ['Deployment', 'StatefulSet', 'ReplicaSet', 'Rollout'];

export default function LiveMigrationOptInPanel({ clusterId }) {
  const [optIns, setOptIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ namespace: '', workload_kind: 'Deployment', workload_name: '' });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/v1/clusters/${clusterId}/live-migration/opt-ins`);
      setOptIns(data.opt_ins || []);
      setError(null);
    } catch (e) {
      setError('Could not load live-migration opt-ins.');
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => { if (clusterId) load(); }, [clusterId, load]);

  const upsert = async (row) => {
    setSaving(true);
    try {
      await axios.put(`${API}/v1/clusters/${clusterId}/live-migration/opt-ins`, row);
      await load();
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    setSaving(true);
    try {
      await axios.delete(`${API}/v1/clusters/${clusterId}/live-migration/opt-ins`, {
        params: { namespace: row.namespace, workload_kind: row.workload_kind, workload_name: row.workload_name },
      });
      await load();
    } catch (e) {
      setError('Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    if (!form.namespace || !form.workload_name) return;
    await upsert({ ...form, enabled: true });
    setForm({ namespace: '', workload_kind: 'Deployment', workload_name: '' });
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <MoveRight className="h-4 w-4 text-indigo-600" />
          Live Migration (per workload)
          <Badge className="bg-indigo-50 text-indigo-700">{optIns.filter((o) => o.enabled).length} opted in</Badge>
        </CardTitle>
        <p className="text-xs text-gray-500 mt-1">
          Opted-in workloads are moved between nodes <span className="font-medium">without restarting</span> during
          consolidations, rebalances and spot interruptions — wherever their pods run. This only changes
          <span className="font-medium"> how pods move</span>; use Spot Policies to control where replicas land.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* add row */}
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs">Namespace</Label>
            <Input className="h-8 w-40" value={form.namespace} placeholder="prod"
                   onChange={(e) => setForm((f) => ({ ...f, namespace: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Kind</Label>
            <select
              className="h-8 w-36 rounded-md border border-slate-200 dark:border-surface-border bg-transparent px-2 text-sm"
              value={form.workload_kind}
              onChange={(e) => setForm((f) => ({ ...f, workload_kind: e.target.value }))}
            >
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Workload</Label>
            <Input className="h-8 w-48" value={form.workload_name} placeholder="checkout-api"
                   onChange={(e) => setForm((f) => ({ ...f, workload_name: e.target.value }))} />
          </div>
          <Button size="sm" onClick={add} disabled={saving || !form.namespace || !form.workload_name}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            <span className="ml-1">Opt in</span>
          </Button>
        </div>

        {error && <div className="text-xs text-red-600">{error}</div>}

        {/* list */}
        {loading ? (
          <div className="text-xs text-gray-500 py-3">Loading…</div>
        ) : !optIns.length ? (
          <div className="text-xs text-gray-500 py-3">
            No workloads opted in yet. Pinned pods (<code>karpenter.sh/do-not-disrupt</code>) are always live-migrated.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-surface-border text-left">
                <th className="py-1.5 px-2 text-xs font-medium uppercase tracking-wide text-gray-500">Workload</th>
                <th className="py-1.5 px-2 text-xs font-medium uppercase tracking-wide text-gray-500">Status</th>
                <th className="py-1.5 px-2" />
              </tr>
            </thead>
            <tbody>
              {optIns.map((o) => (
                <tr key={`${o.namespace}/${o.workload_kind}/${o.workload_name}`}
                    className="border-b border-slate-100 dark:border-slate-800">
                  <td className="py-1.5 px-2">
                    <span className="font-medium">{o.namespace}/{o.workload_name}</span>
                    <span className="ml-1.5 text-xs text-gray-500">{o.workload_kind}</span>
                  </td>
                  <td className="py-1.5 px-2">
                    <button
                      type="button" role="switch" aria-checked={!!o.enabled} disabled={saving}
                      onClick={() => upsert({ ...o, enabled: !o.enabled })}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${o.enabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-slate-700'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${o.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                    <span className="ml-2 text-xs text-gray-500">{o.enabled ? 'live migration' : 'eviction (off)'}</span>
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <button onClick={() => remove(o)} disabled={saving}
                            className="text-gray-400 hover:text-red-600" title="Remove">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
